// Create-banner wizard validation helpers.

import {
  CREATE_BANNER_STEP_NAMES,
  CREATE_BANNER_STEPS,
} from 'src/services/consts/banner/create.const'
import {
  isCatchAllOwnerChosen,
  isDefaultLocaleSelected,
  isPrivacyPolicyUrlValid,
} from 'src/services/helpers/banner/behaviour.helper'
import { isBannerAliasDomainRowInvalid } from 'src/services/helpers/banner/detail.helper'
import { isValidDomain } from 'src/services/helpers/security.helper'

/**
 * Validates the regulations step for the create-banner wizard.
 * Coverage is no longer defaulted by `getDefaultCatchAllOwner`. Step 1 only
 * requires at least one selected regulation. Rest of the World is gated on configure.
 * @param {{ regulations?: string[]|null }} params
 * @returns {boolean}
 */
export function isRegulationsStepValid({ regulations }) {
  return Array.isArray(regulations) && regulations.length > 0
}

/** @param {{ siteName?: string|null, url?: string|null, aliasDomains?: string[] }} banner */
export function isBannerDetailsStepValid({ siteName, url, aliasDomains = [] }) {
  if (!siteName?.trim() || !isValidDomain(url ?? '')) return false
  return aliasDomains.every(
    (row, index) =>
      !row?.trim() ||
      !isBannerAliasDomainRowInvalid({ domain: row, index, aliasDomains, mainUrl: url })
  )
}

/**
 * Configure can create only when at least one language is checked, the default
 * language is one of those, Rest of the World has a still-valid owner, and a
 * privacy-policy URL is either empty or valid.
 * @param {{
 *   defaultLocale?: string,
 *   availableLocales?: string[]|null,
 *   privacyPolicyUrl?: string,
 *   regulations?: string[]|null,
 *   catchAllOwner?: string|null,
 * }} banner
 * @returns {boolean}
 */
export function isConfigureStepValid(banner) {
  if (!banner?.defaultLocale) return false
  if (
    !isDefaultLocaleSelected({
      availableLocales: banner.availableLocales,
      defaultLocale: banner.defaultLocale,
    })
  ) {
    return false
  }
  if (
    !isCatchAllOwnerChosen({
      regulations: banner.regulations,
      catchAllOwner: banner.catchAllOwner,
    })
  ) {
    return false
  }
  return isPrivacyPolicyUrlValid(banner.privacyPolicyUrl)
}

/**
 * Returns true if the user can access `targetStep` given current banner state.
 * All preceding steps must pass validation. The first step is always accessible.
 *
 * @param {string} targetStep - One of CREATE_BANNER_STEP_NAMES values
 * @param {Object} banner - The bannerToEdit state object
 * @returns {boolean}
 */
export function canAccessCreateBannerStep(targetStep, banner) {
  if (
    targetStep === CREATE_BANNER_STEP_NAMES.REGULATIONS ||
    targetStep === CREATE_BANNER_STEP_NAMES.BANNER_CREATED
  ) {
    return true
  }

  const stepValidators = {
    [CREATE_BANNER_STEP_NAMES.REGULATIONS]: () => isRegulationsStepValid(banner),
    [CREATE_BANNER_STEP_NAMES.BANNER_DETAILS]: () => isBannerDetailsStepValid(banner),
    [CREATE_BANNER_STEP_NAMES.CONFIGURE]: () => isConfigureStepValid(banner),
  }

  for (const step of CREATE_BANNER_STEPS) {
    if (step.key === targetStep) return true
    const validate = stepValidators[step.key]
    if (validate && !validate()) return false
  }
  return false
}
